var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isBatchDracoExtension, isFeatureDracoExtension, } from "../../../data/formats/3d_tiles/tiles/pnts";
import { BufferGeometry, Color, Float32BufferAttribute, Uint8BufferAttribute } from "three";
import { DracoLoader } from "../../../data/loaders";
import { constructBufferForNumType, createAttributeBufferFor } from "../../../utils";
import { PickingPointsMaterial } from "../../../materials";
export function readPointCloud(featureTableData, featureTable, batchTableData, batchTable, styleDesc, materialRegistry, objectId) {
    return __awaiter(this, void 0, void 0, function* () {
        // Get the draco extension object from the feature table if it is present and valid
        const featureExtensionData = extractFeatureDracoExtensionObject(featureTable);
        const batchExtensionData = extractBatchDracoExtensionObject(batchTable);
        // Flag whether a per-point colour attribute is present. Needed for handling CONSTANT_RGBA
        const hasPerPointColor = typeof featureTable.attributes.RGB !== "undefined" ||
            typeof featureTable.attributes.RGBA !== "undefined" ||
            typeof featureTable.attributes.RGB565 !== "undefined";
        // Create a new geometry object and decode all draco attributes into it
        let geometry;
        if (featureExtensionData !== undefined) {
            const dracoData = createDracoDataSlice(featureTableData.data, featureExtensionData);
            const decodeOptions = createDracoDecodeOptions(featureTable, featureExtensionData, batchTable, batchExtensionData);
            geometry = yield DracoLoader.decodePointCloudBuffer(decodeOptions, dracoData);
        }
        else {
            geometry = new BufferGeometry();
        }
        // Draco doesn't support compressing all feature table attributes. This filters out all attributes from the feature
        // table that are draco compressed so we can handle the non-compressed attributes.
        const nonDracoFeatureAttributes = Object.entries(featureTable.attributes).filter(([key, _value]) => featureExtensionData === undefined || typeof featureExtensionData.properties[key] === undefined);
        // For all remaining feature attributes we load them as uncompressed data
        for (const [attributeName, byteOffset] of nonDracoFeatureAttributes) {
            loadUncompressedFeatureAttribute(geometry, featureTable, featureTableData.data, attributeName, byteOffset.byteOffset);
        }
        if (typeof batchTable !== "undefined" && typeof batchTableData !== "undefined") {
            const nonDracoBatchFeatureAttributes = Object.entries(batchTable === null || batchTable === void 0 ? void 0 : batchTable.attributes).filter(([key, _value]) => batchExtensionData === undefined || typeof batchExtensionData.properties[key] === undefined);
            for (const [attributeName, attribute] of nonDracoBatchFeatureAttributes) {
                loadUncompressedBatchAttribute(geometry, featureTable, batchTable, batchTableData.data, attributeName, attribute);
            }
        }
        const material = createPointCloudMaterial(featureTable, hasPerPointColor, styleDesc, materialRegistry, objectId);
        // TODO: Can't do this until we have multithreading working
        // // Optimize for raycasting
        // const indices = [];
        // for (let i = 0; i < geometry.attributes.position.count; i++) {
        //     indices.push(i, i, i);
        // }
        // if (indices.length) {
        //     geometry.setIndex(indices);
        //     geometry.computeBoundsTree();
        // }
        return [geometry, material];
    });
}
function extractFeatureDracoExtensionObject(featureTable) {
    // Load the draco point compression extension object. The value will be left as null if an error occurs at any stage
    // of the process
    let extensionData;
    if (typeof featureTable.extensions !== "undefined") {
        if (featureTable.extensions["3DTILES_draco_point_compression"] !== undefined) {
            const data = featureTable.extensions["3DTILES_draco_point_compression"];
            if (isFeatureDracoExtension(data)) {
                extensionData = data;
            }
        }
    }
    return extensionData;
}
function extractBatchDracoExtensionObject(batchTable) {
    if (typeof batchTable === "undefined")
        return undefined;
    // Load the draco point compression extension object. The value will be left as null if an error occurs at any stage
    // of the process
    let extensionData;
    if (typeof batchTable.extensions !== "undefined") {
        if (batchTable.extensions["3DTILES_draco_point_compression"] !== undefined) {
            const data = batchTable.extensions["3DTILES_draco_point_compression"];
            if (isBatchDracoExtension(data)) {
                extensionData = data;
            }
        }
    }
    return extensionData;
}
function createDracoDataSlice(data, extensionData) {
    return new DataView(data.buffer, data.byteOffset + extensionData.byteOffset, extensionData.byteLength);
}
function createDracoDecodeOptions(featureTable, featureExtensionData, batchTable, batchExtensionData) {
    // Iterate through each member in the extension data properties and
    const attributes = {};
    for (const attributeName of Object.keys(featureExtensionData.properties)) {
        if (!(attributeName in featureTable.attributes)) {
            throw new Error("Draco declares data for a semantic not in the feature table");
        }
        switch (attributeName) {
            case "POSITION": {
                attributes.position = {
                    attributeId: featureExtensionData.properties[attributeName],
                    componentType: 8 /* NumType.F32 */,
                    numComponents: 3,
                    normalized: false,
                };
                break;
            }
            case "RGBA": {
                attributes.color = {
                    attributeId: featureExtensionData.properties[attributeName],
                    componentType: 0 /* NumType.U8 */,
                    numComponents: 4,
                    normalized: true,
                    mapperFn: (v) => {
                        v[0] = srgbConv(v[0]);
                        v[1] = srgbConv(v[1]);
                        v[2] = srgbConv(v[2]);
                    },
                };
                break;
            }
            case "RGB": {
                attributes.color = {
                    attributeId: featureExtensionData.properties[attributeName],
                    componentType: 0 /* NumType.U8 */,
                    numComponents: 3,
                    normalized: true,
                    mapperFn: (v) => {
                        v[0] = srgbConv(v[0]);
                        v[1] = srgbConv(v[1]);
                        v[2] = srgbConv(v[2]);
                    },
                };
                break;
            }
            case "NORMAL": {
                attributes.normal = {
                    attributeId: featureExtensionData.properties[attributeName],
                    componentType: 8 /* NumType.F32 */,
                    numComponents: 3,
                    normalized: false,
                };
                break;
            }
            case "BATCH_ID": {
                attributes.BATCH_ID = {
                    attributeId: featureExtensionData.properties[attributeName],
                    componentType: 2 /* NumType.U32 */,
                    numComponents: 1,
                    normalized: false,
                };
                break;
            }
        }
    }
    if (typeof batchTable !== "undefined" && typeof batchExtensionData !== "undefined") {
        for (const attributeName of Object.keys(batchExtensionData.properties)) {
            if (!(attributeName in batchTable.attributes)) {
                throw new Error("Draco declares data for a semantic not in the batch table");
            }
            const attributeInfo = batchTable.attributes[attributeName];
            attributes[attributeName] = {
                attributeId: batchExtensionData.properties[attributeName],
                componentType: attributeInfo.componentType,
                numComponents: attributeInfo.numComponents,
                normalized: false,
            };
        }
    }
    return {
        attributes,
    };
}
function loadUncompressedFeatureAttribute(geometry, featureTable, data, attributeName, byteOffset) {
    switch (attributeName) {
        case "POSITION": {
            const numComponents = 3;
            const size = featureTable.pointsLength * numComponents;
            const begin = data.byteOffset + byteOffset;
            const array = new Float32Array(data.buffer, begin, size);
            geometry.setAttribute("position", new Float32BufferAttribute(array, numComponents));
            break;
        }
        case "POSITION_QUANTIZED": {
            // TODO: How should we support this format? Should we bother?
            throw new Error('Unsupported position format "POSITION_QUANTIZED"');
        }
        case "RGBA": {
            const numComponents = 4;
            const size = featureTable.pointsLength * numComponents;
            const begin = data.byteOffset + byteOffset;
            const array = new Uint8Array(data.buffer, begin, size);
            for (let i = 0; i < array.length; i += numComponents) {
                array[i + 0] = srgbConv(array[i + 0]);
                array[i + 1] = srgbConv(array[i + 1]);
                array[i + 2] = srgbConv(array[i + 2]);
            }
            geometry.setAttribute("color", new Uint8BufferAttribute(array, numComponents, true));
            break;
        }
        case "RGB": {
            const numComponents = 3;
            const size = featureTable.pointsLength * numComponents;
            const begin = data.byteOffset + byteOffset;
            const array = new Uint8Array(data.buffer, begin, size);
            for (let i = 0; i < array.length; i += numComponents) {
                array[i + 0] = srgbConv(array[i + 0]);
                array[i + 1] = srgbConv(array[i + 1]);
                array[i + 2] = srgbConv(array[i + 2]);
            }
            geometry.setAttribute("color", new Uint8BufferAttribute(array, numComponents, true));
            break;
        }
        case "RGB565": {
            // TODO: How should we support this format? Should we bother?
            throw new Error('Unsupported color format "RGB565"');
        }
        case "NORMAL": {
            const numComponents = 3;
            const size = featureTable.pointsLength * numComponents;
            const begin = data.byteOffset + byteOffset;
            const array = new Float32Array(data.buffer, begin, size);
            geometry.setAttribute("normal", new Float32BufferAttribute(array, numComponents));
            break;
        }
        case "NORMAL_OCT16P": {
            // TODO: How should we support this format? Should we bother?
            throw new Error('Unsupported normal format "NORMAL_OCT16P"');
        }
        case "BATCH_ID": {
            const numComponents = 3;
            const size = featureTable.pointsLength * numComponents;
            const begin = data.byteOffset + byteOffset;
            const array = new Float32Array(data.buffer, begin, size);
            geometry.setAttribute("BATCH_ID", new Float32BufferAttribute(array, numComponents));
            break;
        }
        default: {
            throw new Error(`Unknown feature attribute ${attributeName}`);
        }
    }
}
function loadUncompressedBatchAttribute(geometry, featureTable, batchTable, data, attributeName, attribute) {
    const size = featureTable.pointsLength * attribute.numComponents;
    const begin = data.byteOffset + attribute.byteOffset;
    const array = constructBufferForNumType(attribute.componentType, data.buffer, begin, size);
    geometry.setAttribute(attributeName, createAttributeBufferFor(array, attribute.componentType, attribute.numComponents, false));
}
function createPointCloudMaterial(featureTable, hasPerPointColor, styleDesc, materialRegistry, objectId) {
    var _a, _b;
    const opacity = (_a = styleDesc.opacity) !== null && _a !== void 0 ? _a : 1;
    const transparent = opacity < 1;
    const depthWrite = !transparent;
    const params = {
        size: (_b = styleDesc.pointSize) !== null && _b !== void 0 ? _b : 1,
        style: styleDesc.pointStyle,
        objectId: objectId || 0,
        vertexColors: hasPerPointColor,
        clippingPlanes: materialRegistry.clippingPlanes,
        sizeAttenuation: false,
        classificationColors: styleDesc.classificationColors,
        intensityColors: styleDesc.intensityColors,
        gradientColors: styleDesc.gradientColors,
        brightness: styleDesc.brightness,
        contrast: styleDesc.contrast,
        gamma: styleDesc.gamma,
        forceSinglePass: true,
        opacity,
        transparent,
        depthWrite,
    };
    if (featureTable.constantRGBA !== undefined) {
        params.color = new Color(featureTable.constantRGBA[0], featureTable.constantRGBA[1], featureTable.constantRGBA[2]);
    }
    return new PickingPointsMaterial(params);
}
function srgbConv(n) {
    return Math.trunc(Math.pow(n / 255, 2.2) * 255);
}
